/**
 * <s-cart> manages all cart quantity updates and removals via the command pattern.
 * It fetches partials after each mutation so the cart drawer, cart page, and header
 * counters all stay in sync — do not convert to a full page reload or direct DOM manipulation.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --update-quantity — Adjusts quantity by [data-delta] for [data-line-key]; debounced 200ms per line.
 *   --remove          — Cancels queued/in-flight work for [data-line-key], then sets quantity to 0.
 *   --apply-discount  — Applies the value of the descendant [data-discount-code] input.
 *
 * Required child markup (queried by selector/attribute):
 *   [data-variant-id]                                — Visual/fallback identifier for a line's row and controls.
 *   [data-line-key]                                  — Primary cart line identity ({{ item.key }}); sent to updateCart as the line id.
 *   [data-delta]                                     — Integer delta (±N) paired with --update-quantity.
 *   [data-qty-min]                                   — Minimum quantity from variant.quantity_rule.min (default 1).
 *   [data-qty-max]                                   — Maximum quantity from variant.quantity_rule.max (when absent, no max).
 *   [data-qty-step]                                  — Increment step from variant.quantity_rule.increment (default 1).
 *   [data-quantity-display][data-line-key]           — Input/element holding the current quantity (value or textContent).
 *   [data-discount-code]                             — Discount-code input read by --apply-discount and Enter.
 *   [style*="view-transition-name"]                  — Row wrapper used for removal focus-restore + view transitions.
 *
 * Element dataset (attributes on <s-cart> itself):
 *   data-partials — Comma-separated partial names to re-fetch (default: "cart-items,cart-count").
 *                   Must match {% partial 'name' %} blocks in cart.liquid / theme.liquid / cart-drawer.liquid.
 *
 * External coupling:
 *   Cart feedback (#sr-announcer + s-notification:add) is owned by the updateCart
 *   handler in standard-actions-override.js — do not duplicate here.
 */
import { partials } from '@shopify/partial-rendering';
import { transition } from '@theme/view-transition';
import { normalizeQuantity, quantityRulesFromDataset } from '@theme/quantity-rules';
import { ViewEventElement } from '@theme/s-view-event';

const GIFT_POPUP_KEY = 'tb-free-gift-popup';

class CartItems extends ViewEventElement {
  static #pendingFocusStates = new Map();
  static #refreshRevision = 0;

  #lifecycleAc;
  #quantityAcs = new Map();
  #discountAc;
  #noteAc;
  #rafId;
  #debounceTimers = new Map();
  #rollbackValues = new Map();
  #openDetails = new Set();

  // ---------------------------------------------------------------------
  // Automatic free gift (logic ported from the live theme's cart-drawer.js
  // config.giftVariantId / maybeSyncGift). Config comes from
  // <script type="application/json" data-free-gift-config> in the layout:
  //   { "variantId": "53323043373363", "threshold": 3 }
  // Rule: when the summed quantity of all non-gift lines reaches the threshold,
  // add exactly 1 gift unit via /cart/add.js (never duplicated); when it drops
  // below, remove the gift via /cart/change.js (quantity 0). Runs after every
  // cart refresh and once on page load.
  // ---------------------------------------------------------------------
  static #giftAddFailed = false;
  static #giftInitDone = false;

  static #giftConfig() {
    const el = document.querySelector('script[data-free-gift-config]');
    if (!el) return null;
    try {
      const config = JSON.parse(el.textContent || '{}');
      const variantId = String(config.variantId || '').trim();
      if (!variantId) return null;
      const vapeIds = new Set((Array.isArray(config.vapeVariantIds) ? config.vapeVariantIds : []).map((id) => String(id).trim()).filter(Boolean));
      const vapeThreshold = Number(config.vapeThreshold) > 0 ? Number(config.vapeThreshold) : 0;
      return { variantId, threshold: Number(config.threshold) > 0 ? Number(config.threshold) : 3, vapeIds, vapeThreshold };
    } catch {
      return null;
    }
  }

  static #cartRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}${path}`;
  }

  static async #cartJson(path, body) {
    const response = await fetch(CartItems.#cartRoute(path), body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  static #showGiftPopup() {
    try {
      if (window.sessionStorage.getItem(GIFT_POPUP_KEY)) return;
      window.sessionStorage.setItem(GIFT_POPUP_KEY, '1');
    } catch {
      /* storage unavailable: still show once for this add */
    }
    const dialog = document.getElementById('free-gift-modal');
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
  }

  static #clearGiftPopupFlag() {
    try {
      window.sessionStorage.removeItem(GIFT_POPUP_KEY);
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------------
  // One queue for every cart mutation and gift sync. Standard Actions updateCart
  // (standard-actions-override.js), chooseVape and the gift sync all run through
  // runExclusive, one at a time, so a gift sync can never interleave with a removal
  // and leave a stale row on screen. syncFreeGift coalesces: at most one sync waits
  // in the queue; it always re-renders the cart page and drawer when it finishes and
  // re-checks that no removed line is still displayed.
  // ---------------------------------------------------------------------
  static #queue = Promise.resolve();
  static #giftQueued = null;

  static runExclusive(task) {
    const run = CartItems.#queue.then(() => task());
    CartItems.#queue = run.catch(() => {});
    return run;
  }

  static syncFreeGift({ initial = false } = {}) {
    if (CartItems.#giftQueued) return CartItems.#giftQueued;
    const queued = CartItems.runExclusive(async () => {
      CartItems.#giftQueued = null;
      await CartItems.#syncFreeGiftNow({ initial });
    });
    CartItems.#giftQueued = queued;
    return queued;
  }

  /** Line keys shown in mounted carts that are no longer in the real cart. */
  static #staleLineKeys(cart) {
    const keys = new Set((cart?.items || []).map((item) => String(item.key)));
    const shown = new Set();
    for (const el of document.querySelectorAll('s-cart [data-line-key]')) {
      if (el.dataset.lineKey) shown.add(el.dataset.lineKey);
    }
    return [...shown].filter((key) => !keys.has(key));
  }

  /** Re-render; if a removed line is still displayed, re-render again, then reload as a last resort. */
  static async #renderAndVerify({ force = true } = {}) {
    let cart = null;
    try {
      cart = await CartItems.#cartJson('cart.js');
    } catch {
      cart = null;
    }
    const staleBefore = cart ? CartItems.#staleLineKeys(cart).length > 0 : false;
    if (!force && !staleBefore) return;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await CartItems.refreshMountedCarts({ skipGiftSync: true });
      } catch {
        /* checked below */
      }
      try {
        cart = await CartItems.#cartJson('cart.js');
      } catch {
        return;
      }
      if (!CartItems.#staleLineKeys(cart).length) return;
    }
    window.location.reload();
  }

  static async #syncFreeGiftNow({ initial = false } = {}) {
    const config = CartItems.#giftConfig();
    if (!config) {
      await CartItems.#renderAndVerify({ force: !initial });
      return;
    }
    let changed = false;
    let added = false;
    try {
      const cart = await CartItems.#cartJson('cart.js');
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const giftLines = items.filter((item) => String(item.variant_id) === config.variantId);
      // Gift lines (gummies jar, free vape, any $0 "free-gift" product) never count toward the 3-item rule.
      const paidUnits = items.reduce((sum, item) => (
        CartItems.#isGiftItem(item, config) ? sum : sum + (Number(item.quantity) || 0)
      ), 0);

      // Free vape: remove when the eligible subtotal is under the threshold; keep exactly one unit otherwise.
      // It is only added when the shopper picks a flavor (chooseVape).
      if (config.vapeIds.size && config.vapeThreshold) {
        const vapeLines = items.filter((item) => config.vapeIds.has(String(item.variant_id)));
        if (vapeLines.length) {
          if (CartItems.#vapeEligible(items, config) < config.vapeThreshold) {
            for (const line of vapeLines) await CartItems.#cartJson('cart/change.js', { id: line.key, quantity: 0 });
            changed = true;
          } else {
            const [first, ...extra] = vapeLines;
            if (Number(first.quantity) !== 1) {
              await CartItems.#cartJson('cart/change.js', { id: first.key, quantity: 1 });
              changed = true;
            }
            for (const line of extra) {
              await CartItems.#cartJson('cart/change.js', { id: line.key, quantity: 0 });
              changed = true;
            }
          }
        }
      }

      if (paidUnits >= config.threshold) {
        if (!giftLines.length) {
          if (!CartItems.#giftAddFailed) {
            try {
              await CartItems.#cartJson('cart/add.js', { items: [{ id: Number(config.variantId), quantity: 1 }] });
              changed = true;
              added = true;
            } catch {
              // Out of stock or unavailable: do not retry on every refresh this page view.
              CartItems.#giftAddFailed = true;
            }
          }
        } else {
          // Keep exactly one gift unit: first gift line at 1, any extra gift lines at 0.
          const [first, ...extra] = giftLines;
          if (Number(first.quantity) !== 1) {
            await CartItems.#cartJson('cart/change.js', { id: first.key, quantity: 1 });
            changed = true;
          }
          for (const line of extra) {
            await CartItems.#cartJson('cart/change.js', { id: line.key, quantity: 0 });
            changed = true;
          }
        }
      } else if (giftLines.length) {
        for (const line of giftLines) {
          await CartItems.#cartJson('cart/change.js', { id: line.key, quantity: 0 });
        }
        changed = true;
        CartItems.#clearGiftPopupFlag();
      } else {
        CartItems.#giftAddFailed = false;
      }

    } catch {
      /* The gift sync must never break the cart UI. */
    }
    // Always re-render after a sync triggered by a cart change (on first page load only when something changed or a row is stale).
    await CartItems.#renderAndVerify({ force: changed || !initial });
    if (added) CartItems.#showGiftPopup();
  }

  static #isGiftItem(item, config) {
    const variant = String(item?.variant_id);
    if (variant === config.variantId || config.vapeIds.has(variant)) return true;
    return String(item?.handle || '').includes('free-gift') && Number(item?.final_price) === 0;
  }

  /**
   * Eligible subtotal for the free vape, in cents: paid lines after quantity-tier discounts
   * (final_line_price), before discount codes such as WELCOME10 (code allocations added back),
   * gift lines excluded. Mirrors snippets/cart-vape-reward.liquid.
   */
  static #vapeEligible(items, config) {
    let total = 0;
    for (const item of items) {
      if (CartItems.#isGiftItem(item, config) || Number(item.final_price) === 0) continue;
      let line = Number(item.final_line_price) || 0;
      for (const alloc of item.line_level_discount_allocations || []) {
        if (alloc?.discount_application?.type === 'discount_code') line += Number(alloc.amount) || 0;
      }
      total += line;
    }
    return total;
  }

  /** Adds the chosen free-vape flavor (qty 1) or swaps the current one. Ignored under the threshold. */
  static async chooseVape(variantId) {
    const config = CartItems.#giftConfig();
    const chosen = String(variantId || '');
    if (!config || !config.vapeIds.has(chosen)) return;
    await CartItems.runExclusive(async () => {
      try {
        const cart = await CartItems.#cartJson('cart.js');
        const items = Array.isArray(cart?.items) ? cart.items : [];
        if (CartItems.#vapeEligible(items, config) < config.vapeThreshold) return;
        const vapeLines = items.filter((item) => config.vapeIds.has(String(item.variant_id)));
        const updates = {};
        for (const line of vapeLines) updates[String(line.variant_id)] = 0;
        updates[chosen] = 1;
        await CartItems.#cartJson('cart/update.js', { updates });
      } catch {
        /* keep the cart as it was */
      }
    });
    // Queued after the swap: enforces qty 1 and re-renders the cart page and drawer.
    await CartItems.syncFreeGift();
  }

  /** Runs a final gift sync after everything already queued and resolves when the cart is settled (used before jumping to checkout). */
  static async ensureFreeGiftSynced() {
    await CartItems.syncFreeGift();
    await CartItems.#queue;
  }

  static async refreshMountedCarts({ signal, skipGiftSync = false } = {}) {
    const revision = ++CartItems.#refreshRevision;
    signal?.throwIfAborted();

    const carts = [...document.querySelectorAll('s-cart')].filter((cart) => cart instanceof CartItems);
    const partialNames = [...new Set(carts.flatMap((cart) => cart.#partialNames))];

    if (!partialNames.length) return;

    // Capture UI state before partial rendering replaces the DOM.
    const focusStates = new Map();
    for (const cart of carts) {
      cart.#saveTransientState();
      focusStates.set(
        cart.id,
        CartItems.#pendingFocusStates.get(cart.id) ?? cart.#refreshFocusState()
      );
    }

    let html;
    try {
      // @partial-fetch-contract attributes=data-partials fallback=cart-items,cart-count
      html = await partials.fetch(...partialNames, { signal });
    } catch (error) {
      signal?.throwIfAborted();
      if (revision !== CartItems.#refreshRevision) return;
      throw error;
    }

    signal?.throwIfAborted();
    if (revision !== CartItems.#refreshRevision) return;

    let applied = false;
    try {
      await transition(() => {
        if (revision !== CartItems.#refreshRevision) return;
        signal?.throwIfAborted();
        partials.apply(html);
        applied = true;
      });
    } catch (error) {
      signal?.throwIfAborted();
      if (revision !== CartItems.#refreshRevision) return;
      throw error;
    }

    signal?.throwIfAborted();
    if (!applied || revision !== CartItems.#refreshRevision) return;

    for (const previousCart of carts) {
      const cart = previousCart.#mountedCart();
      cart.#openDetails = new Set(previousCart.#openDetails);
      cart.#reconcileAfterRefresh();
      const focusState = focusStates.get(previousCart.id);
      cart.#restoreRefreshFocus(focusState);
      if (CartItems.#pendingFocusStates.get(previousCart.id) === focusState) {
        CartItems.#pendingFocusStates.delete(previousCart.id);
      }
    }

    signal?.throwIfAborted();
    if (!skipGiftSync) CartItems.syncFreeGift();
  }

  connectedCallback() {
    super.connectedCallback();

    if (!CartItems.#giftInitDone) {
      CartItems.#giftInitDone = true;
      queueMicrotask(() => CartItems.syncFreeGift({ initial: true }));
    }

    this.#lifecycleAc = new AbortController();
    const opts = { signal: this.#lifecycleAc.signal };

    this.addEventListener('command', (e) => {
      const variantId = e.source.dataset.variantId;
      const lineKey = e.source.dataset.lineKey;

      if (e.command === '--update-quantity') {
        if (!lineKey) return;
        const display = this.#quantityDisplayForLine(lineKey, variantId);
        if (!display) return;
        const current = parseInt(display.value ?? display.textContent, 10) || 0;
        const delta = parseInt(e.source.dataset.delta, 10) || 0;
        const newQuantity = normalizeQuantity(
          current + delta,
          quantityRulesFromDataset(e.source),
          { allowZero: true }
        );

        display.value = newQuantity;
        const focusTarget = { lineKey, variantId, delta: e.source.dataset.delta };
        if (!this.#debounceTimers.has(lineKey)) this.#rollbackValues.set(lineKey, current);
        clearTimeout(this.#debounceTimers.get(lineKey));
        const rollbackValue = this.#rollbackValues.get(lineKey);
        this.#debounceTimers.set(lineKey, setTimeout(() => {
          this.#debounceTimers.delete(lineKey);
          this.#rollbackValues.delete(lineKey);
          this.#updateCart(lineKey, variantId, newQuantity, focusTarget, () => {
            const el = this.#quantityDisplayForLine(lineKey, variantId);
            if (el) el.value = rollbackValue;
          });
        }, 200));
      }

      if (e.command === '--remove') {
        if (!lineKey) return;
        this.#updateCart(lineKey, variantId, 0);
      }

      if (e.command === '--apply-discount') {
        this.#applyDiscount(this.querySelector('[data-discount-code]')?.value);
      }
    }, opts);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches?.('[data-discount-code]')) {
        e.preventDefault();
        this.#applyDiscount(e.target.value);
      }
    }, opts);

    this.addEventListener('change', (e) => {
      const input = e.target;

      if (input.matches('[data-quantity-display]')) {
        const variantId = input.dataset.variantId;
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;
        const parsed = parseInt(input.value, 10);
        const quantity = normalizeQuantity(
          parsed,
          quantityRulesFromDataset(input),
          { allowZero: true }
        );

        // Capture the pre-edit committed value for rollback on mutation failure.
        // defaultValue reflects the server-committed state maintained by #reconcileAfterRefresh.
        const committedValue = input.defaultValue;
        input.value = quantity;
        this.#updateCart(lineKey, variantId, quantity, { lineKey, variantId, input: true }, () => {
          const el = this.#quantityDisplayForLine(lineKey, variantId);
          if (el) el.value = committedValue;
        });
      } else if (input.id === 'cart-note') {
        this.#updateNote(input.value);
      }
    }, opts);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleAc?.abort();
    for (const controller of this.#quantityAcs.values()) controller.abort();
    this.#quantityAcs.clear();
    this.#discountAc?.abort();
    this.#noteAc?.abort();
    for (const timer of this.#debounceTimers.values()) clearTimeout(timer);
    this.#debounceTimers.clear();
    this.#rollbackValues.clear();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #findLineElement(selector, lineKey, variantId = null) {
    for (const el of this.querySelectorAll(selector)) {
      if (lineKey && el.dataset.lineKey === lineKey) return el;
    }

    if (!variantId) return null;
    for (const el of this.querySelectorAll(selector)) {
      if (el.dataset.variantId === variantId) return el;
    }

    return null;
  }

  #quantityDisplayForLine(lineKey, variantId) {
    return this.#findLineElement('[data-quantity-display]', lineKey, variantId);
  }

  #findCartRow(lineKey, variantId) {
    return this.#findLineElement('[data-line-key], [data-variant-id]', lineKey, variantId)
      ?.closest('[style*="view-transition-name"]');
  }

  #mountedCart() {
    if (this.isConnected) return this;

    const replacement = this.id ? this.ownerDocument.getElementById(this.id) : null;
    return replacement instanceof CartItems ? replacement : this;
  }

  #refreshFocusState() {
    const active = this.ownerDocument.activeElement;
    if (!active?.matches || !this.contains(active)) return null;

    if (active.matches('[data-discount-code], button[command="--apply-discount"]')) {
      return { discountTarget: active.matches('[data-discount-code]') ? 'input' : 'button' };
    }

    const lineKey = active.dataset.lineKey;
    const variantId = active.dataset.variantId;
    if (active.matches('button[command="--remove"]')) {
      const row = this.#findCartRow(lineKey, variantId);
      const sibling = row?.nextElementSibling || row?.previousElementSibling;
      const siblingLine = sibling?.querySelector('[data-line-key], [data-variant-id]');
      return {
        isRemoval: true,
        siblingLineKey: siblingLine?.dataset.lineKey,
        siblingVariantId: siblingLine?.dataset.variantId,
      };
    }

    if (active.matches('button[data-delta], [data-quantity-display]')) {
      return {
        focusTarget: {
          lineKey,
          variantId,
          delta: active.dataset.delta,
          input: active.matches('[data-quantity-display]'),
        },
      };
    }

    return null;
  }

  #restoreRefreshFocus(state) {
    if (!state) return;
    const { isRemoval, siblingLineKey, siblingVariantId, focusTarget, discountTarget } = state;

    if (isRemoval) {
      this.#focusAfterRemoval(siblingLineKey, siblingVariantId);
    } else if (discountTarget) {
      const selector = discountTarget === 'input'
        ? '[data-discount-code]'
        : 'button[command="--apply-discount"]';
      this.querySelector(selector)?.focus();
    } else {
      this.#focusTarget(focusTarget)?.focus();
    }
  }

  #focusTarget({ lineKey, variantId, delta, input } = {}) {
    const selector = input ? '[data-quantity-display]' : 'button[data-delta]';
    let variantFallback = null;

    for (const el of this.querySelectorAll(selector)) {
      const deltaMatches = input || delta == null || el.dataset.delta === delta;
      if (!deltaMatches) continue;
      if (lineKey && el.dataset.lineKey === lineKey) return el;
      if (!variantFallback && variantId && el.dataset.variantId === variantId) {
        variantFallback = el;
      }
    }

    return variantFallback;
  }

  #focusAfterRemoval(siblingLineKey, siblingVariantId) {
    const freshRow = siblingLineKey || siblingVariantId
      ? this.#findCartRow(siblingLineKey, siblingVariantId)
      : null;
    const siblingControl = freshRow?.querySelector('a, button');
    if (siblingControl) {
      siblingControl.focus();
      return;
    }

    const anyControl = this.querySelector('a, button');
    if (anyControl) {
      anyControl.focus();
      return;
    }

    const enclosingDialog = this.closest('dialog[open]');
    const closeButton = enclosingDialog?.querySelector('button[command="close"]');
    if (closeButton) {
      closeButton.focus();
      return;
    }

    const main = document.getElementById('main-content') || this.closest('main');
    main?.focus();
  }

  #restoreFocusAfterMutation(focusState) {
    const { isRemoval, siblingLineKey, siblingVariantId, focusTarget } = focusState;
    if (CartItems.#pendingFocusStates.get(this.id) === focusState) {
      CartItems.#pendingFocusStates.delete(this.id);
    }

    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      const cart = this.#mountedCart();

      if (isRemoval) {
        cart.#focusAfterRemoval(siblingLineKey, siblingVariantId);
      } else if (focusTarget) {
        cart.#focusTarget(focusTarget)?.focus();
      }

      this.#rafId = null;
    });
  }

  #abortQuantityMutation(lineKey) {
    // A queued debounce has no AbortSignal yet, so cancel it explicitly before
    // aborting active work. This ensures an immediate remove or input commit wins.
    clearTimeout(this.#debounceTimers.get(lineKey));
    this.#debounceTimers.delete(lineKey);
    this.#rollbackValues.delete(lineKey);

    const controller = this.#quantityAcs.get(lineKey);
    controller?.abort();
    this.#quantityAcs.delete(lineKey);
  }

  #settleQuantityMutation(lineKey, controller) {
    if (this.#quantityAcs.get(lineKey) !== controller) return;

    this.#quantityAcs.delete(lineKey);
    if (this.#quantityAcs.size === 0) this.removeAttribute('aria-busy');
  }

  get #partialNames() {
    // dataset.partials mirrors the Liquid-written data-partials="..." attribute.
    // The fallback names ('cart-items', 'cart-count') must stay in sync with the
    // {% partial %} block names in cart.liquid and theme.liquid (cart-count) — renaming either breaks cart updates.
    const names = this.dataset.partials;
    return names ? names.split(',').map((s) => s.trim()) : ['cart-items', 'cart-count'];
  }

  #saveTransientState() {
    this.#openDetails.clear();
    for (const details of this.querySelectorAll('details[open]')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id) this.#openDetails.add(id);
    }
  }

  #reconcileAfterRefresh() {
    for (const input of this.querySelectorAll('[data-quantity-display]')) {
      if ((input instanceof HTMLInputElement)) {
        // partials.apply() preserves form control state, but cart quantities are
        // server-owned after a cart refresh and should reflect the new markup.
        input.value = input.defaultValue;
      }
    }

    // Restore <details> open state lost during partial rendering
    for (const details of this.querySelectorAll('details')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id && this.#openDetails.has(id)) details.open = true;
    }
    this.#openDetails.clear();
  }

  async #updateCart(lineKey, variantId, quantity, focusTarget = null, onRollback = null) {
    // Cancel queued and in-flight work for this line so the latest action wins.
    this.#abortQuantityMutation(lineKey);
    const controller = new AbortController();
    this.#quantityAcs.set(lineKey, controller);
    const { signal } = controller;

    this.setAttribute('aria-busy', 'true');

    const isRemoval = quantity === 0;
    const row = isRemoval ? this.#findCartRow(lineKey, variantId) : null;
    const sibling = row?.nextElementSibling || row?.previousElementSibling;
    const siblingLine = sibling?.querySelector('[data-line-key], [data-variant-id]');
    const siblingLineKey = siblingLine?.dataset.lineKey;
    const siblingVariantId = siblingLine?.dataset.variantId;
    const pendingFocusState = {
      isRemoval,
      siblingLineKey,
      siblingVariantId,
      focusTarget,
    };
    if (this.id) CartItems.#pendingFocusStates.set(this.id, pendingFocusState);

    try {
      // Cart mutation goes through Standard Actions (Storefront API). The shared
      // updateCart override in standard-actions-override.js triggers a partial refresh
      // (cart-items, cart-count, etc.) via CartItems.refreshMountedCarts() after the
      // mutation resolves. The {% partial 'name' %} blocks in cart.liquid, theme.liquid,
      // and snippets/cart-drawer.liquid remain the source of truth for what gets re-rendered.
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ id: lineKey, quantity }] },
        {
          event: { context: 'cart' },
          signal,
        }
      );

      const userError = result?.userErrors?.[0]?.message;
      if (userError) {
        throw new Error(userError, {
          cause: { userErrors: result.userErrors },
        });
      }

      const replacementApplied = signal.aborted && this.#mountedCart() !== this;
      if (signal.aborted && !replacementApplied) return;
      if (!signal.aborted) this.#settleQuantityMutation(lineKey, controller);

      // The Standard Actions override owns feedback: non-toast success uses
      // #sr-announcer, while any toast owns its live role. Avoid a local duplicate.
      this.#restoreFocusAfterMutation(pendingFocusState);
    } catch (error) {
      const replacementApplied = signal.aborted && this.#mountedCart() !== this;
      if (replacementApplied) {
        this.#restoreFocusAfterMutation(pendingFocusState);
        return;
      }
      if (CartItems.#pendingFocusStates.get(this.id) === pendingFocusState) {
        CartItems.#pendingFocusStates.delete(this.id);
      }
      if (signal.aborted) return;
      onRollback?.();
      this.#settleQuantityMutation(lineKey, controller);
      // Cart error toast feedback is owned by the Standard Actions override.
      // Don't duplicate the announcement on [data-cart-status].
    }
  }

  async #applyDiscount(code) {
    code = code?.trim();
    if (!code) return;

    this.#discountAc?.abort();
    this.#discountAc = new AbortController();
    const { signal } = this.#discountAc;

    this.setAttribute('aria-busy', 'true');
    try {
      const result = await window.Shopify.actions.updateCart(
        { discountCodes: [code] },
        { event: { context: 'cart' }, signal }
      );
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError, { cause: { userErrors: result.userErrors } });
      if (signal.aborted) return;
      // Typing WELCOME10 in the field counts as accepting it, so the welcome popup's guard keeps it.
      if (code.toUpperCase() === 'WELCOME10') {
        try { localStorage.setItem('tb-welcome10-choice', JSON.stringify({ status: 'accepted', source: 'manual', at: Date.now() })); } catch { /* storage unavailable */ }
      }
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

  async #updateNote(note) {
    this.#noteAc?.abort();
    this.#noteAc = new AbortController();
    const { signal } = this.#noteAc;

    this.setAttribute('aria-busy', 'true');
    try {
      await window.Shopify.actions.updateCart(
        { note },
        { event: { context: 'cart' }, signal }
      );
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

}

if (!customElements.get('s-cart')) {
  customElements.define('s-cart', CartItems);
}

// Public hook for other components (e.g. the Best Offers picker) to settle the free gift before leaving for checkout.
// Free vape flavor picker (snippets/cart-vape-reward.liquid), in the drawer and on /cart.
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-vape-choice]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;
  if (button.getAttribute('aria-pressed') === 'true') return;
  event.preventDefault();
  const grid = button.closest('[data-vape-grid]');
  grid?.setAttribute('aria-busy', 'true');
  for (const other of grid?.querySelectorAll('[data-vape-choice]') || []) other.disabled = true;
  CartItems.chooseVape(button.dataset.vapeChoice).finally(() => {
    grid?.removeAttribute('aria-busy');
    for (const other of grid?.querySelectorAll('[data-vape-choice]') || []) other.disabled = false;
  });
});

window.TBFreeGift = window.TBFreeGift || {};
window.TBFreeGift.ensureSynced = () => CartItems.ensureFreeGiftSynced();
